import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from "react-router-dom";
import SubNav from "../components/nav";
import SEO from '../components/SEO';
import { ArrowRight, PenTool, BookOpen, Edit3, Download, BarChart2 } from 'lucide-react';
import '../styles/ScriptToUse.css';
import { baseUrl } from '../config';



const HowToUsePreProduction = () => {
    const sectionRefs = useRef([]);
    const [activeSection, setActiveSection] = useState(0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = sectionRefs.current.indexOf(entry.target);
                        setActiveSection(index);
                    }
                });
            },
            { threshold: 0.3 }
        );

        const currentRefs = sectionRefs.current;
        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
            observer.disconnect();
        };
    }, []);

    const sections = [
        {
            title: "Generate Ideas",
            content: `Unleash Your Creativity with Our AI-Powered Story Generator. Whether you're looking to craft a romantic drama, a thrilling mystery, or a sci-fi adventure, 
                    our AI-powered generator opens up endless creative possibilities. You can input themes, genres, or specific character traits and receive fresh, unique story ideas instantly. 
                    The tool encourages experimentation, letting you combine different settings and genres to explore a wide range of creative suggestions that align with your vision.`,
            details: `Tip: Combine unexpected themes or settings, such as a fantasy set in a futuristic dystopia, to see how the AI responds. Often, the most unconventional ideas lead to the most captivating results.`,
            icon: <PenTool size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="generate-ideas-video" src="https://www.youtube.com/embed/watch?v=_GuOjXYl5ew" frameBorder="0"></iframe>
        },
        {
            title: "Write Prompts",
            content: `Crafting the right prompt is key to getting the best output from the AI. Our tutorial helps you understand how to write prompts that 
                    guide the AI toward specific plot elements, character arcs, and scenes. Learn how to balance giving the AI direction while leaving room 
                    for creative AI interpretation.`,
            details: `Example: For a compelling AI-generated scene, try starting with a prompt like "A determined detective confronts a suspect in a rainy alleyway, 
                    but the truth is more complex than it seems."`,
            icon: <BookOpen size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="write-prompts-video" src="https://www.youtube.com/embed/watch?v=_GuOjXYl5ew" frameBorder="0"></iframe>
        },
        {
            title: "Edit AI-Generated Scripts",
            content: `Once you've generated a script, it's time to refine and polish it. Our editing suite offers advanced tools to help you tweak dialogue, 
                    enhance pacing, and adjust the tone to match your desired style. With real-time editing capabilities, you can quickly make improvements 
                    and see the results instantly.`,
            details: `Tip: Use the tone adjuster to switch between formal, conversational, or quirky styles to see how it changes the overall feel of a scene.`,
            icon: <Edit3 size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="edit-scripts-video" src="https://www.youtube.com/embed/watch?v=_GuOjXYl5ew" frameBorder="0"></iframe>
        },
        {
            title: "Download and View Scripts",
            content: `Download your scripts in various formats (PDF, Word, etc.) and view them in an optimized, scrollable format. Share your work easily 
                    with collaborators or keep a well-organized archive of your projects. Whether you're on a mobile device or desktop, our responsive 
                    script viewer ensures a smooth reading experience.`,
            details: `Note: You can also export your scripts in industry-standard formats, ready for pitching or further development.`,
            icon: <Download size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="download-scripts-video" src="https://www.youtube.com/embed/watch?v=_GuOjXYl5ew" frameBorder="0"></iframe>
        },
        {
            title: "Analyze Script Metrics",
            content: `Our analytics tools offer valuable insights into your script's structure. Analyze key metrics like word count, character dialogue distribution, 
                    and scene breakdowns. This data helps you understand the pacing, balance character interactions, and make sure that the story flows naturally.`,
            details: `For instance, if you notice that one character dominates the dialogue, you can revise scenes to balance character interactions.`,
            icon: <BarChart2 size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="analyze-metrics-video" src="https://www.youtube.com/embed/watch?v=_GuOjXYl5ew" frameBorder="0"></iframe>
        }
    ];

    return (
        <>
            <SEO
              title="Script Analysis Guide"
              description="Learn how to use ProducerBuddy's AI script analysis tool to break down scenes, identify production challenges, and optimise your screenplay."
              url="https://producerbuddy.pilomation.com/scriptAnalysisHowTo"
            />
            <SubNav />
            <div className="how-to-use">
                <div className="how-to-use__container">
                    <h1 className="how-to-use__title">How to Use Producer Buddy: Pre-Production</h1>

                    <div className="how-to-use__layout">
                        {/* Sidebar */}
                        <div className="how-to-use__sidebar">
                            {sections.map((section, index) => (
                                <div
                                    key={index}
                                    className={`how-to-use__sidebar-item ${activeSection === index ? 'active' : ''}`}
                                    aria-current={activeSection === index ? 'page' : undefined}
                                >
                                    {section.title}
                                </div>
                            ))}
                        </div>

                        {/* Timeline */}
                        <div className="how-to-use__timeline">
                            {sections.map((section, index) => (
                                <div
                                    key={index}
                                    ref={(el) => (sectionRefs.current[index] = el)}
                                    className={`how-to-use__section ${activeSection === index ? 'how-to-use__animate-in' : ''}`}
                                >
                                    <div className="how-to-use__section-icon">{section.icon}</div>
                                    <div className="how-to-use__section-content">
                                        <h2 className="how-to-use__section-title">{section.title}</h2>
                                        <p className="how-to-use__section-text">{section.content}</p>
                                        <p className="how-to-use__section-details">{section.details}</p>
                                    </div>
                                    <img
                                        className="how-to-use__section-image"
                                        src={section.img}
                                        alt={`${section.title} illustration`}
                                    />
                                    {section.iframe}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="how-to-use__cta-container">
                        <NavLink to="/Services" className="how-to-use__cta-button">
                            Explore Features
                            <ArrowRight className="ml-2" />
                        </NavLink>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HowToUsePreProduction;

